import {
    BuildingPrimaryPropertyType,
    type BuildingPrimaryPropertyType as BuildingPrimaryPropertyTypeValue,
} from '../../core-models';

export const BuildingPrimaryPropertyTypeName = {
    [BuildingPrimaryPropertyType.HouseTownhouseVilla]:
        'House / Townhouse / Villa',
    [BuildingPrimaryPropertyType.DuplexSemiDetached]: 'Duplex / Semi-detached',
    [BuildingPrimaryPropertyType.BoardingHouseGuestHouse]:
        'Boarding house / Guest house',
    [BuildingPrimaryPropertyType.ApartmentUnitBlock]: 'Apartment / Unit block',
    [BuildingPrimaryPropertyType.HotelMotelHostel]: 'Hotel / Motel / Hostel',
    [BuildingPrimaryPropertyType.CaretakersResidenceWithinNonResidential]:
        "Caretaker's residence (within non-residential)",
    [BuildingPrimaryPropertyType.AgedCareFacility]: 'Aged care facility',
    [BuildingPrimaryPropertyType.RetirementVillage]: 'Retirement village',
    [BuildingPrimaryPropertyType.OfficeBuilding]: 'Office building',
    [BuildingPrimaryPropertyType.GpMedicalPractice]: 'GP / Medical practice',
    [BuildingPrimaryPropertyType.RetailShop]: 'Retail shop',
    [BuildingPrimaryPropertyType.RestaurantCafeBar]: 'Restaurant / Cafe / Bar',
    [BuildingPrimaryPropertyType.Showroom]: 'Showroom',
    [BuildingPrimaryPropertyType.ShoppingCentre]: 'Shopping centre',
    [BuildingPrimaryPropertyType.CarPark]: 'Car park',
    [BuildingPrimaryPropertyType.WarehouseStorage]: 'Warehouse / Storage',
    [BuildingPrimaryPropertyType.FactoryManufacturing]:
        'Factory / Manufacturing',
    [BuildingPrimaryPropertyType.LaboratoryWorkshop]: 'Laboratory / Workshop',
    [BuildingPrimaryPropertyType.HospitalHealthcareFacility]:
        'Hospital / Healthcare facility',
    [BuildingPrimaryPropertyType.DaySurgeryMedicalCentre]:
        'Day surgery / Medical centre',
    [BuildingPrimaryPropertyType.SchoolEducation]: 'School / Education',
    [BuildingPrimaryPropertyType.ChildcareCentre]: 'Childcare centre',
    [BuildingPrimaryPropertyType.UniversityTafe]: 'University / TAFE',
    [BuildingPrimaryPropertyType.TheatreAssemblyHall]:
        'Theatre / Assembly hall',
    [BuildingPrimaryPropertyType.ChurchPlaceOfWorship]:
        'Church / Place of worship',
    [BuildingPrimaryPropertyType.CommunityHall]: 'Community hall',
    [BuildingPrimaryPropertyType.SportsFacilityStadium]:
        'Sports facility / Stadium',
    [BuildingPrimaryPropertyType.TransportTerminal]: 'Transport terminal',
    [BuildingPrimaryPropertyType.LibraryMuseumGallery]:
        'Library / Museum / Gallery',
    [BuildingPrimaryPropertyType.ShedGarageCarport]: 'Shed / Garage / Carport',
    [BuildingPrimaryPropertyType.SwimmingPoolEnclosure]:
        'Swimming pool enclosure',
    [BuildingPrimaryPropertyType.FenceRetainingWall]: 'Fence / Retaining wall',
    [BuildingPrimaryPropertyType.PrivateBushfireShelter]:
        'Private bushfire shelter',
} as const;

export type BuildingPrimaryPropertyTypeName =
    (typeof BuildingPrimaryPropertyTypeName)[BuildingPrimaryPropertyTypeValue];

export function getBuildingPrimaryPropertyTypeName(
    type: BuildingPrimaryPropertyTypeValue
): string {
    return BuildingPrimaryPropertyTypeName[type];
}

export const BuildingPrimaryPropertyTypeGroupLabel = {
    Residential: 'Residential',
    Commercial: 'Commercial',
    Industrial: 'Industrial',
    Community: 'Community & Public',
    Other: 'Other Structures',
} as const;

export type BuildingPrimaryPropertyTypeGroupLabel =
    (typeof BuildingPrimaryPropertyTypeGroupLabel)[keyof typeof BuildingPrimaryPropertyTypeGroupLabel];

export const BuildingPrimaryPropertyTypeGroup: Record<
    BuildingPrimaryPropertyTypeValue,
    BuildingPrimaryPropertyTypeGroupLabel
> = {
    [BuildingPrimaryPropertyType.HouseTownhouseVilla]:
        BuildingPrimaryPropertyTypeGroupLabel.Residential,
    [BuildingPrimaryPropertyType.DuplexSemiDetached]:
        BuildingPrimaryPropertyTypeGroupLabel.Residential,
    [BuildingPrimaryPropertyType.BoardingHouseGuestHouse]:
        BuildingPrimaryPropertyTypeGroupLabel.Residential,
    [BuildingPrimaryPropertyType.ApartmentUnitBlock]:
        BuildingPrimaryPropertyTypeGroupLabel.Residential,
    [BuildingPrimaryPropertyType.HotelMotelHostel]:
        BuildingPrimaryPropertyTypeGroupLabel.Residential,
    [BuildingPrimaryPropertyType.CaretakersResidenceWithinNonResidential]:
        BuildingPrimaryPropertyTypeGroupLabel.Residential,
    [BuildingPrimaryPropertyType.AgedCareFacility]:
        BuildingPrimaryPropertyTypeGroupLabel.Residential,
    [BuildingPrimaryPropertyType.RetirementVillage]:
        BuildingPrimaryPropertyTypeGroupLabel.Residential,
    [BuildingPrimaryPropertyType.OfficeBuilding]:
        BuildingPrimaryPropertyTypeGroupLabel.Commercial,
    [BuildingPrimaryPropertyType.GpMedicalPractice]:
        BuildingPrimaryPropertyTypeGroupLabel.Commercial,
    [BuildingPrimaryPropertyType.RetailShop]:
        BuildingPrimaryPropertyTypeGroupLabel.Commercial,
    [BuildingPrimaryPropertyType.RestaurantCafeBar]:
        BuildingPrimaryPropertyTypeGroupLabel.Commercial,
    [BuildingPrimaryPropertyType.Showroom]:
        BuildingPrimaryPropertyTypeGroupLabel.Commercial,
    [BuildingPrimaryPropertyType.ShoppingCentre]:
        BuildingPrimaryPropertyTypeGroupLabel.Commercial,
    [BuildingPrimaryPropertyType.CarPark]:
        BuildingPrimaryPropertyTypeGroupLabel.Industrial,
    [BuildingPrimaryPropertyType.WarehouseStorage]:
        BuildingPrimaryPropertyTypeGroupLabel.Industrial,
    [BuildingPrimaryPropertyType.FactoryManufacturing]:
        BuildingPrimaryPropertyTypeGroupLabel.Industrial,
    [BuildingPrimaryPropertyType.LaboratoryWorkshop]:
        BuildingPrimaryPropertyTypeGroupLabel.Industrial,
    [BuildingPrimaryPropertyType.HospitalHealthcareFacility]:
        BuildingPrimaryPropertyTypeGroupLabel.Community,
    [BuildingPrimaryPropertyType.DaySurgeryMedicalCentre]:
        BuildingPrimaryPropertyTypeGroupLabel.Community,
    [BuildingPrimaryPropertyType.SchoolEducation]:
        BuildingPrimaryPropertyTypeGroupLabel.Community,
    [BuildingPrimaryPropertyType.ChildcareCentre]:
        BuildingPrimaryPropertyTypeGroupLabel.Community,
    [BuildingPrimaryPropertyType.UniversityTafe]:
        BuildingPrimaryPropertyTypeGroupLabel.Community,
    [BuildingPrimaryPropertyType.TheatreAssemblyHall]:
        BuildingPrimaryPropertyTypeGroupLabel.Community,
    [BuildingPrimaryPropertyType.ChurchPlaceOfWorship]:
        BuildingPrimaryPropertyTypeGroupLabel.Community,
    [BuildingPrimaryPropertyType.CommunityHall]:
        BuildingPrimaryPropertyTypeGroupLabel.Community,
    [BuildingPrimaryPropertyType.SportsFacilityStadium]:
        BuildingPrimaryPropertyTypeGroupLabel.Community,
    [BuildingPrimaryPropertyType.TransportTerminal]:
        BuildingPrimaryPropertyTypeGroupLabel.Community,
    [BuildingPrimaryPropertyType.LibraryMuseumGallery]:
        BuildingPrimaryPropertyTypeGroupLabel.Community,
    [BuildingPrimaryPropertyType.ShedGarageCarport]:
        BuildingPrimaryPropertyTypeGroupLabel.Other,
    [BuildingPrimaryPropertyType.SwimmingPoolEnclosure]:
        BuildingPrimaryPropertyTypeGroupLabel.Other,
    [BuildingPrimaryPropertyType.FenceRetainingWall]:
        BuildingPrimaryPropertyTypeGroupLabel.Other,
    [BuildingPrimaryPropertyType.PrivateBushfireShelter]:
        BuildingPrimaryPropertyTypeGroupLabel.Other,
} as const;

export interface BuildingPrimaryPropertyTypeOption {
    value: BuildingPrimaryPropertyTypeValue;
    label: BuildingPrimaryPropertyTypeName;
    group: BuildingPrimaryPropertyTypeGroupLabel;
}

export interface BuildingPrimaryPropertyTypeOptionGroup {
    label: BuildingPrimaryPropertyTypeGroupLabel;
    options: ReadonlyArray<BuildingPrimaryPropertyTypeOption>;
}

const BUILDING_PRIMARY_PROPERTY_TYPE_GROUP_ORDER: ReadonlyArray<BuildingPrimaryPropertyTypeGroupLabel> =
    [
        BuildingPrimaryPropertyTypeGroupLabel.Residential,
        BuildingPrimaryPropertyTypeGroupLabel.Commercial,
        BuildingPrimaryPropertyTypeGroupLabel.Industrial,
        BuildingPrimaryPropertyTypeGroupLabel.Community,
        BuildingPrimaryPropertyTypeGroupLabel.Other,
    ];

const BUILDING_PRIMARY_PROPERTY_TYPE_OPTIONS: ReadonlyArray<BuildingPrimaryPropertyTypeOption> =
    Object.values(BuildingPrimaryPropertyType)
        .filter(
            (value): value is BuildingPrimaryPropertyTypeValue =>
                typeof value === 'number'
        )
        .map((value) => ({
            value,
            label: BuildingPrimaryPropertyTypeName[value],
            group: BuildingPrimaryPropertyTypeGroup[value],
        }));

const BUILDING_PRIMARY_PROPERTY_TYPE_OPTION_GROUPS: ReadonlyArray<BuildingPrimaryPropertyTypeOptionGroup> =
    BUILDING_PRIMARY_PROPERTY_TYPE_GROUP_ORDER.map((groupLabel) => ({
        label: groupLabel,
        options: BUILDING_PRIMARY_PROPERTY_TYPE_OPTIONS.filter(
            (option) => option.group === groupLabel
        ).sort((a, b) => a.label.localeCompare(b.label)),
    }));

export function getBuildingPrimaryPropertyTypeOptionGroups(): ReadonlyArray<BuildingPrimaryPropertyTypeOptionGroup> {
    return BUILDING_PRIMARY_PROPERTY_TYPE_OPTION_GROUPS;
}
